import React, { useEffect, useState } from 'react'
import List from 'react-collection-manager'
import { Meteor } from 'meteor/meteor'
import moment from 'moment'
import { toast } from 'react-toastify'

import Switch from '@material-ui/core/Switch'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Print from '@material-ui/icons/Print'

import 'uniforms-bridge-simple-schema-2'
import Select from '../../components/Select'
import handleSuccess from '../../lib/backoffice/handleSuccess'
import schema from '../../schema/invoices'

const handleError = (reason: string) => toast.error(reason.error)

const ListInvoices = () => {
  const [invoices, setInvoices] = useState([])
  const [editInvoices, setEditInvoices] = useState(false)
  const [pdfInvoices, setPdfInvoices] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectYear, setSelectYear] = useState(moment().format('Y'))

  useEffect(() => {
    fetchInvoices(selectYear)
  }, [selectYear])

  const listYear = []
  for (let i = 2017; i <= moment().format('Y'); i++) {
    listYear.push({ _id: String(i), name: String(i) })
  }

  const fetchInvoices = async (year) => {
    setLoading(true)
    const invoicesFetched = await Meteor.promise('invoices.read', {
      paid: 'all',
      year: year,
      displayList: true,
    }).catch((error) => toast.error(error.message))
    setInvoices(
      invoicesFetched.invoices.map((c) => {
        const year = moment(c.date, 'DD/MM/YYYY').format('YYYY-')
        const invoiceNr = String(c.invoiceNr)
        return {
          ...c,
          type: c.pro ? 'pro' : 'part',
          paid: c.paid ? 'paid' : 'unpaid',
          payment_type: !!c.payment_type ? c.payment_type : 'cb',
          invoiceNr: `${year}${invoiceNr.padStart(3, '0')}`,
        }
      })
    )
    setLoading(false)
  }

  const schemaFilled = schema.extend({
    status: {
      label: 'Status',
      type: String,
      uniforms: {
        options: [
          { label: 'Payé', value: 'paid' },
          { label: 'Non Payé', value: 'unpaid' },
          { label: 'Abandonnée et/ou Annulée', value: 'abandoned' },
        ],
      },
    },
  })

  const updateMethod = async (invoice) => {
    await Meteor.promise('invoices.update', invoice)
    await fetchInvoices(selectYear)
  }

  const configuration = (
    <div>
      <FormControlLabel
        control={<Switch checked={editInvoices} onChange={() => setEditInvoices(!editInvoices)} />}
        label="Mode Edition"
      />{' '}
      <FormControlLabel
        control={<Switch checked={pdfInvoices} onChange={() => setPdfInvoices(!pdfInvoices)} />}
        label="Génération PDF"
      />
    </div>
  )

  const moreActions = []
  if (!editInvoices && !pdfInvoices) {
    moreActions.push((rowData) => ({
      icon: () => <Print color={'secondary'} />,
      tooltip: 'Imprimer la facture',
      onClick: (event, rowData) => handleGenerate(rowData),
    }))
  }

  const generatePdfMethod = async (invoiceId) => {
    setLoading(true)
    await Meteor.promise('invoices.generatePdf', invoiceId)
    fetchInvoices(selectYear)
    setPdfInvoices(false)
  }
  if (!editInvoices && !!pdfInvoices) {
    moreActions.push((rowData) => ({
      icon: () => <Print color={'primary'} />,
      tooltip: 'Générer le pdf de la facture',
      onClick: (event, rowData) => generatePdfMethod(rowData._id),
      hidden: !!rowData.invoiceUrl,
    }))
  }

  const columns = [
    {
      name: 'Client',
      property: 'customerName',
      filtering: true,
    },
    {
      name: 'Numéro de facture',
      property: 'invoiceNr',
      defaultSort: 'desc',
      filtering: true,
    },
    {
      name: 'Date',
      property: 'date',
      defaultSort: 'desc',
      customSort: (a, b) => {
        const aM = moment(a.date, 'DD/MM/YYYY')
        const bM = moment(b.date, 'DD/MM/YYYY')

        if (aM.isBefore(bM)) return 1
        else if (aM.isAfter(bM)) return -1
        return 0
      },
      type: 'date',
    },
    {
      name: 'Type de client',
      property: 'type',
      lookup: { pro: 'Pro', part: 'Particuler' },
    },
    {
      name: 'Type de paiement',
      property: 'payment_type',
      lookup: { cb: 'CB', sepa: 'SEPA' },
      hidden: true,
    },
    {
      name: 'Status',
      property: 'status',
      lookup: { paid: 'Payé', unpaid: 'non Payé', abandoned: 'Abandonnée et/ou Annulée' },
      filtering: true,
    },
    {
      name: 'Montant TTC',
      property: 'amount',
      type: 'currency',
      filtering: false,
      currencySetting: { locale: 'fr', currencyCode: 'EUR' },
    },
    {
      name: 'Reste à payer TTC',
      property: 'amountToPay',
      type: 'currency',
      filtering: false,
      hidden: true,
      currencySetting: { locale: 'fr', currencyCode: 'EUR' },
    },
    { name: 'id', property: '_id', hidden: true, filtering: true },
  ]
  const handleGenerate = (rowData) => {
    rowData.length > 0 &&
      rowData.forEach(async (invoice) => {
        await fetch(invoice.invoiceUrl)
          .then((resp) => resp.arrayBuffer())
          .then((resp) => {
            // set the blog type to final pdf
            const file = new Blob([resp], { type: 'application/pdf' })

            // process to auto download it
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(file)
            link.download = `Facture_N°${invoice.invoiceNr}.pdf`
            link.click()
          })
      })
  }

  return (
    <div style={{ width: 'calc(100% - 32px)' }}>
      <Select
        optionsList={listYear}
        handleSelectValue={setSelectYear}
        selectedValue={selectYear}
        label={'Année'}
      />
      <List
        title="factures"
        lang="fr"
        className="page"
        loading={loading}
        columns={columns}
        entries={invoices}
        schema={schemaFilled}
        updateMethod={updateMethod}
        canEdit={!!editInvoices && !pdfInvoices}
        onError={handleError}
        onSuccess={handleSuccess}
        options={{
          exportButton: true,
          exportAllData: true,
          maxBodyHeight: 'calc(100vh - 290px)',
          padding: 'dense',
          selection: !editInvoices && !pdfInvoices,
          selectionProps: (rowData) => ({
            disabled: !rowData.invoiceUrl,
          }),
        }}
        configuration={configuration}
        moreActions={moreActions}
      />
    </div>
  )
}

export default ListInvoices
